// @flow
import { Record, List, Map } from "immutable"
import type { Map as MapType } from "immutable"
import { reset } from "redux-form"

import * as watchdogApi from "./watchdogApi"
import * as accountApi from "./accountApi"
import { INIT, LOADING, SUCCESS, ERROR } from "common/consts/phase"
import { LOGOUT_SUCCESS } from "common/services/user"
import { getLanguageInfo } from "common/services/staticData"
import type { WatchdogInterval } from "common/consts/account"
import type Watchdog from "../records/Watchdog"
import type { ApiRequestStateType } from "common/types/api"
import type {
  BookingFiltersValuesProps,
  BookingFiltersValuesType,
  TransactionFiltersValuesProps,
  TransactionFiltersValuesType,
  FiltersValues,
  FilterStateType,
  InitialStateType,
  InitialStateInst,
  SetBookingFiltersAction,
  SetTransactionFiltersAction,
  Action,
  GetState,
} from "./AccountDuck.types"
import { API_LIMIT } from "../consts/api"

const SET_BOOKING_FILTER = "account/SET_BOOKING_FILTER"
const SET_TRANSACTION_FILTER = "account/SET_TRANSACTION_FILTER"

const LOAD_BOOKINGS = "account/LOAD_BOOKINGS"
const LOAD_BOOKINGS_REQUEST = "account/LOAD_BOOKINGS_REQUEST"
const LOAD_BOOKINGS_SUCCESS = "account/LOAD_BOOKINGS_SUCCESS"
const LOAD_BOOKINGS_FAILURE = "account/LOAD_BOOKINGS_FAILURE"

const LOAD_WATCHDOGS = "account/LOAD_WATCHDOGS"
const LOAD_WATCHDOGS_REQUEST = "account/LOAD_WATCHDOGS_REQUEST"
const LOAD_WATCHDOGS_SUCCESS = "account/LOAD_WATCHDOGS_SUCCESS"
const LOAD_WATCHDOGS_FAILURE = "account/LOAD_WATCHDOGS_FAILURE"

const UPDATE_WATCHDOG = "account/UPDATE_WATCHDOG"
// const UPDATE_WATCHDOG_REQUEST = "account/UPDATE_WATCHDOG_REQUEST"
const UPDATE_WATCHDOG_SUCCESS = "account/UPDATE_WATCHDOG_SUCCESS"
const UPDATE_WATCHDOG_FAILURE = "account/UPDATE_WATCHDOG_FAILURE"

const ENABLE_WATCHDOG = "account/ENABLE_WATCHDOG"
const ENABLE_WATCHDOG_REQUEST = "account/ENABLE_WATCHDOG_REQUEST"
const ENABLE_WATCHDOG_FAILURE = "account/ENABLE_WATCHDOG_FAILURE"

const DISABLE_WATCHDOG = "account/DISABLE_WATCHDOG"
const DISABLE_WATCHDOG_REQUEST = "account/DISABLE_WATCHDOG_REQUEST"
// const DISABLE_WATCHDOG_SUCCESS = "account/DISABLE_WATCHDOG_SUCCESS"
const DISABLE_WATCHDOG_FAILURE = "account/DISABLE_WATCHDOG_FAILURE"

const REMOVE_WATCHDOG = "account/REMOVE_WATCHDOG"
// const REMOVE_WATCHDOG_REQUEST = "account/REMOVE_WATCHDOG_REQUEST"
const REMOVE_WATCHDOG_SUCCESS = "account/REMOVE_WATCHDOG_SUCCESS"
const REMOVE_WATCHDOG_FAILURE = "account/REMOVE_WATCHDOG_FAILURE"

const LOAD_TRANSACTIONS = "account/LOAD_TRANSACTIONS"
const LOAD_TRANSACTIONS_REQUEST = "account/LOAD_TRANSACTIONS_REQUEST"
const LOAD_TRANSACTIONS_SUCCESS = "account/LOAD_TRANSACTIONS_SUCCESS"
const LOAD_TRANSACTIONS_FAILURE = "account/LOAD_TRANSACTIONS_FAILURE"

const DataState: ApiRequestStateType<*> = Record({
  data: null,
  phase: INIT,
  error: null,
})

const BookingFilters: BookingFiltersValuesType = new Record({
  dateFrom: null,
  dateTo: null,
  status: null,
  limit: API_LIMIT,
  offset: 0,
})

const TransactionFilters: TransactionFiltersValuesType = new Record({
  dateFrom: null,
  dateTo: null,
  type: null,
})

const FiltersState: FilterStateType = new Record({
  bookings: new BookingFilters(),
  transactions: new TransactionFilters(),
})

const InitialState: InitialStateType = new Record({
  showFilters: false,
  bookings: new DataState({ data: new List() }),
  watchdogs: new DataState({ data: new Map() }),
  transactions: new DataState({ data: new List() }),
  filters: new FiltersState(),
  noMoreBookings: false,
})

function toInitialState(map: MapType): InitialStateInst {
  return new InitialState({
    ...map.toJS(),
    bookings: new DataState(map.get("bookings")),
    watchdogs: new DataState(map.get("watchdogs")),
    transactions: new DataState(map.get("transactions")),
    filters: new FiltersState(),
  })
}

export default function accountReducer(
  state: ?InitialStateInst = new InitialState(),
  action: Action,
) {
  if (!(state instanceof InitialState)) return toInitialState(state)

  switch (action.type) {
    case LOGOUT_SUCCESS:
      return new InitialState()

    case SET_BOOKING_FILTER:
      return state
        .setIn(["filters", "bookings"], new BookingFilters(action.values))
        .setIn(["bookings", "phase"], INIT) // tells the component it needs an update

    case SET_TRANSACTION_FILTER:
      return state
        .setIn(["filters", "transactions"], new TransactionFilters(action.values))
        .setIn(["transactions", "phase"], INIT) // tells the component it needs an update

    // bookings
    // ---

    case LOAD_BOOKINGS_REQUEST:
      return state.setIn(["bookings", "phase"], LOADING)

    case LOAD_BOOKINGS_SUCCESS: {
      let bookings
      // load more bookings
      if (action.payload.filters.get("offset") !== 0) {
        bookings = state.getIn(["bookings", "data"]).concat(action.res)
      } else {
        // first load or change filter
        bookings = action.res
      }

      return state
        .setIn(["bookings", "phase"], SUCCESS)
        .setIn(["bookings", "data"], bookings)
        .set("noMoreBookings", action.res.size === 0)
    }

    case LOAD_BOOKINGS_FAILURE:
      return state
        .setIn(["bookings", "phase"], ERROR)
        .setIn(["bookings", "error"], String(action.error))

    // watchdogs
    // ---

    // requests
    case LOAD_WATCHDOGS_REQUEST:
      return state.setIn(["watchdogs", "phase"], LOADING)

    case ENABLE_WATCHDOG_REQUEST:
      return state.setIn(["watchdogs", "data", action.payload.id, "active"], true)

    case DISABLE_WATCHDOG_REQUEST:
      return state.setIn(["watchdogs", "data", action.payload.id, "active"], false)

    // failures
    case LOAD_WATCHDOGS_FAILURE:
    case UPDATE_WATCHDOG_FAILURE:
    case REMOVE_WATCHDOG_FAILURE:
      return state
        .setIn(["watchdogs", "phase"], ERROR)
        .setIn(["watchdogs", "error"], String(action.error))

    case ENABLE_WATCHDOG_FAILURE:
      return state
        .setIn(["watchdogs", "phase"], ERROR)
        .setIn(["watchdogs", "error"], String(action.error))
        .setIn(["watchdogs", "data", action.payload.id, "active"], false)

    case DISABLE_WATCHDOG_FAILURE:
      return state
        .setIn(["watchdogs", "phase"], ERROR)
        .setIn(["watchdogs", "error"], String(action.error))
        .setIn(["watchdogs", "data", action.payload.id, "active"], true)

    // success
    case LOAD_WATCHDOGS_SUCCESS:
      return state.setIn(["watchdogs", "phase"], SUCCESS).setIn(["watchdogs", "data"], action.res)

    case UPDATE_WATCHDOG_SUCCESS: {
      return state.setIn(
        ["watchdogs", "data", action.payload.id, "interval"],
        action.payload.interval,
      )
    }

    case REMOVE_WATCHDOG_SUCCESS:
      return state
        .setIn(["watchdogs", "phase"], SUCCESS)
        .deleteIn(["watchdogs", "data", action.payload.id])

    // transactions
    // ---

    case LOAD_TRANSACTIONS_REQUEST:
      return state.setIn(["transactions", "phase"], LOADING)

    case LOAD_TRANSACTIONS_SUCCESS:
      return state
        .setIn(["transactions", "phase"], SUCCESS)
        .setIn(["transactions", "data"], action.res)

    case LOAD_TRANSACTIONS_FAILURE:
      return state
        .setIn(["transactions", "phase"], ERROR)
        .setIn(["transactions", "error"], String(action.error))

    default:
      return state
  }
}

export const setBookingFilters = (values: BookingFiltersValuesProps): SetBookingFiltersAction => ({
  type: SET_BOOKING_FILTER,
  values,
})

export const setTransactionFilters = (
  values: TransactionFiltersValuesProps,
): SetTransactionFiltersAction => ({
  type: SET_TRANSACTION_FILTER,
  values,
})

export const loadBookings = (filters: FiltersValues) => (
  dispatch: Dispatch<*>,
  getState: GetState,
) =>
  dispatch({
    type: LOAD_BOOKINGS,
    promise: accountApi.loadBookings(getLanguageInfo(getState()).api, filters),
    payload: { filters },
    _track: { filters },
  })

export const loadWatchdogs = (lang: string) => (dispatch: Dispatch<*>, getState: GetState) => {
  const langInfo = getLanguageInfo(getState())
  return dispatch({
    type: LOAD_WATCHDOGS,
    promise: accountApi.loadWatchdogs(langInfo.translations, langInfo.iso),
    _track: { lang },
  })
}

export const updateWatchdog = (watchdog: Watchdog, interval: WatchdogInterval) => (
  dispatch: Dispatch<*>,
  getState: GetState,
) =>
  dispatch({
    type: UPDATE_WATCHDOG,
    promise: watchdogApi.updateWatchdog(watchdog, interval, getLanguageInfo(getState()).api),
    payload: { id: watchdog.id, watchdog, interval },
    _track: { watchdog, interval },
  })

export const setActivityForWatchdog = (watchdog: Watchdog, active: boolean) => (
  dispatch: Dispatch<*>,
  getState: GetState,
) =>
  dispatch({
    type: active ? ENABLE_WATCHDOG : DISABLE_WATCHDOG,
    promise: watchdogApi.setActivityForWatchdog(watchdog, active, getLanguageInfo(getState()).api),
    payload: { id: watchdog.id },
    _track: { id: watchdog.id },
  })

export const removeWatchdog = (watchdog: Watchdog) => (dispatch: Dispatch<*>, getState: GetState) =>
  dispatch({
    type: REMOVE_WATCHDOG,
    promise: watchdogApi.removeWatchdog(watchdog, getLanguageInfo(getState()).api),
    payload: { id: watchdog.id },
    _track: { id: watchdog.id },
  })

export const loadTransactions = (filters: FiltersValues) => ({
  type: LOAD_TRANSACTIONS,
  promise: accountApi.loadTransactions(filters),
  _track: { filters },
})

export const resetFilters = () => (dispatch: Dispatch<*>) => {
  dispatch(reset("accountFilters"))
}
